import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import AddAPhoto from "@mui/icons-material/AddAPhoto";
import Logo from "../../assets/logoStudioPBADS.png";
import { guidGenerator, useViewport } from "../../helpers";
import clsx from "clsx";
import Paper from "@mui/material/Paper";
import Carousel from "react-material-ui-carousel";
import { Check, Close } from "@mui/icons-material";
import { Box, Button, Dialog } from "@mui/material";
import DndKit from "./DndKit";
import ImageLinkable from "./draggableElements/ImageLinkable.jsx";
import SearchContent from "../../views/adminDmaHome/components/SearchContent.jsx";

const PREFIX = "HeaderImageManager";

const classes = {
  mainContainer: `${PREFIX}-mainContainer`,
  draggableList: `${PREFIX}-draggableList`,
  sectionTitle: `${PREFIX}-sectionTitle`,
  sectionTitleStyle: `${PREFIX}-sectionTitleStyle`,
  fileInput: `${PREFIX}-fileInput`,
  carouselContainer: `${PREFIX}-carouselContainer`,
  carouselImage: `${PREFIX}-carouselImage`,
  carouselMainContainer: `${PREFIX}-carouselMainContainer`,
  displayRow: `${PREFIX}-displayRow`,
  displayColumn: `${PREFIX}-displayColumn`,
  iconButtonAddPicture: `${PREFIX}-iconButtonAddPicture`,
  iconButtonAddPictureResponsive: `${PREFIX}-iconButtonAddPictureResponsive`,
  iconSizeAddPicture: `${PREFIX}-iconSizeAddPicture`,
  iconPosition: `${PREFIX}-iconPosition`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.mainContainer}`]: {
    display: "flex",
    flexDirection: "row",
    width: "90%",
    [theme.breakpoints.between("xs", "md")]: {
      flexDirection: "column",
      width: "100%",
    },
    margin: "1rem 0",
  },

  [`& .${classes.draggableList}`]: {
    height: "130px",
    display: "flex",
    marginRight: "1rem",
    overflow: "auto",
    padding: "0.5rem 0.4rem",
    width: "90%",
    backgroundColor: "#424242",
    [theme.breakpoints.between("xs", "md")]: {
      marginTop: "0",
      height: "105px",
      width: "100%",
    },
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
  },

  [`& .${classes.sectionTitle}`]: {
    textAlign: "center",
  },

  [`& .${classes.sectionTitleStyle}`]: {
    color: theme.palette.primary.main,
    marginBottom: "1rem",
  },

  [`& .${classes.fileInput}`]: {
    display: "none",
  },

  [`& .${classes.carouselContainer}`]: {
    height: "300px",
    maxWidth: "300px",
    margin: "0 auto",
  },

  [`& .${classes.carouselImage}`]: {
    maxHeight: "300px",
    width: "100%",
    position: "relative",
    left: "50%",
    transform: "translate(-50%)",
  },

  [`& .${classes.carouselMainContainer}`]: {
    margin: "0 auto",
    width: "100%",
    [theme.breakpoints.between("xs", "md")]: {
      width: "100%",
    },
  },

  [`& .${classes.displayRow}`]: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    width: "100%",
  },

  [`& .${classes.displayColumn}`]: {
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.iconButtonAddPicture}`]: {
    position: "relative",
    top: "50%",
    transform: "translateY(-50%)",
  },

  [`& .${classes.iconButtonAddPictureResponsive}`]: {
    position: "relative",
    left: "50%",
    transform: "translateX(-50%)",
    marginTop: "1rem",
  },

  [`& .${classes.iconSizeAddPicture}`]: {
    width: "50px",
    height: "50px",
  },

  [`& .${classes.iconPosition}`]: {
    width: "10%",
    [theme.breakpoints.between("xs", "md")]: {
      width: "100%",
    },
  },
}));

function DragAndDropList({ setterDDList, setHeadersUrl, ddList, ...props }) {
  const { width } = useViewport();
  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [type, setType] = useState(null);

  useEffect(() => {
    const existingPictures = [];
    setLoading(true);
    if (ddList.hasOwnProperty(props.id) && ddList[props.id].length > 0) {
      ddList[props.id].forEach((picture) => {
        const guid = guidGenerator();
        existingPictures.push({
          ...picture,
          id: guid,
          alt: `header-dnd-small-${guid}`,
        });
      });
    }
    setElements(existingPictures);
    setHeadersUrl(existingPictures);
  }, []);

  function handleAddElement(event) {
    const tmp = [...elements];
    let i = 0;
    for (i; i < event.target.files.length; i++) {
      const guid = guidGenerator();
      tmp.push({
        id: guid,
        file: event.target.files[i],
        url: URL.createObjectURL(event.target.files[i]),
        alt: `header-dnd-small-${guid}`,
      });
    }
    setElements(tmp);
    setHeadersUrl(tmp);
    setterDDList({ ...ddList, [props.id]: tmp });
  }

  function handleUpdateElement(selectedFromSearch) {
    const tmp = [...elements];
    const elt = tmp.find((elt) => elt.id === open);
    elt.type = type;
    if (type === "fanartzone" || type === "vipartzone") {
      elt.objectId = selectedFromSearch.uniqueKey;
    } else {
      elt.objectId = selectedFromSearch.id;
    }
    setElements(tmp);
    setHeadersUrl(tmp);
    setterDDList({ ...ddList, [props.id]: tmp });
    setOpen(null);
    setType(null);
  }

  function handleUnlink(id) {
    const tmp = [...elements];
    const elt = tmp.find((elt) => elt.id === id);
    elt.type = null;
    elt.objectId = null;
    setElements(tmp);
    setHeadersUrl(tmp);
    setterDDList({ ...ddList, [props.id]: tmp });
  }

  function handleRemoveFromDraggable(elem, index) {
    const tmp = [...elements];
    tmp.splice(index, 1);
    setElements(tmp);
    setHeadersUrl(tmp);
    setterDDList({ ...ddList, [props.id]: tmp });
  }

  function Item(props) {
    const [url, setUrl] = useState(props.item.url ? props.item.url : Logo);

    return (
      <Root>
        <Paper elevation={12} className={classes.carouselContainer}>
          <img
            src={url}
            alt={props.item.id}
            className={classes.carouselImage}
            onError={(e) => setUrl(Logo)}
            onLoad={() => setLoading(false)}
          />
        </Paper>
      </Root>
    );
  }

  return (
    <Root>
      <Paper
        elevation={3}
        style={{
          padding: "1rem",
          marginBottom: "2rem",
          backgroundColor: "#424242",
        }}
      >
        <Typography
          variant="h5"
          className={clsx(
            width >= 960 && classes.sectionTitle,
            classes.sectionTitleStyle
          )}
        >
          {props.sectionTitle}
        </Typography>
        <div className={classes.carouselMainContainer}>
          <Carousel className="Carousel-header-dnd">
            {elements.map((item, i) => (
              <Item key={i + 1} item={item} />
            ))}
          </Carousel>
        </div>
        <div
          className={clsx(
            width >= 960 ? classes.displayRow : classes.displayColumn
          )}
        >
          <div className={classes.iconPosition}>
            <input
              accept="image/*"
              className={classes.fileInput}
              id="icon-button-file"
              type="file"
              multiple
              onChange={(e) => handleAddElement(e)}
            />
            <label htmlFor="icon-button-file">
              <IconButton
                color="primary"
                aria-label="upload picture"
                component="span"
                className={clsx(
                  width >= 960
                    ? classes.iconButtonAddPicture
                    : classes.iconButtonAddPictureResponsive
                )}
                size="large"
              >
                <AddAPhoto className={classes.iconSizeAddPicture} />
              </IconButton>
            </label>
          </div>
          <div className={classes.mainContainer}>
            <Paper
              elevation={10}
              className={classes.draggableList}
              sx={{ marginBottom: "2rem", padding: " 0.3rem 0 0 0" }}
            >
              <DndKit
                mode={"horizontal"}
                data={elements}
                reorderedArray={(newArray) => {
                  setElements(newArray);
                  setHeadersUrl(newArray);
                  setterDDList({ ...ddList, [props.id]: newArray });
                }}
                renderItem={(elem, index) => (
                  <Box key={`header-element-${index + 1}`}>
                    <ImageLinkable
                      element={elem}
                      onLink={() => setOpen(elem.id)}
                      onUnlink={() => handleUnlink(elem.id)}
                      onRemove={() => handleRemoveFromDraggable(elem, index)}
                    />
                  </Box>
                )}
              />
            </Paper>
          </div>
        </div>
        <Dialog
          onClose={() => setOpen(null)}
          open={open != null}
          title="type to link"
        >
          <div
            style={{ textAlign: "center", padding: "2rem", minWidth: "300px" }}
          >
            {!type && (
              <div>
                <div style={{ margin: "1rem" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    title="EXP-AR-IENCE"
                    onClick={() => setType("exparience")}
                  >
                    EXP-AR-IENCE
                  </Button>
                </div>
                <div style={{ margin: "1rem" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    title="SPHERE"
                    onClick={() => setType("entity")}
                  >
                    SPHERE
                  </Button>
                </div>
                <div style={{ margin: "1rem" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    title="FANartZONE"
                    onClick={() => setType("fanartzone")}
                  >
                    FANarZONE
                  </Button>
                </div>
                <div style={{ margin: "1rem" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    title="VIPartZONE"
                    onClick={() => setType("vipartzone")}
                  >
                    VIPartZONE
                  </Button>
                </div>
              </div>
            )}
            {type && <LinkObjects type={type} searchApis={props.searchApis} />}
          </div>
        </Dialog>
      </Paper>
    </Root>
  );

  function LinkObjects(args) {
    switch (args.type) {
      case "exparience":
        return (
          <LinkObject
            id="expariences"
            type={args.type}
            isAsync
            searchApi={args.searchApis[0]}
          />
        );
      case "entity":
        return (
          <LinkObject
            id="entities"
            type={args.type}
            isAsync
            searchApi={args.searchApis[1]}
          />
        );
      case "fanartzone":
        return (
          <LinkObject
            id="fan"
            type={args.type}
            options={args.searchApis[2]("FAN")}
          />
        );
      case "vipartzone":
        return (
          <LinkObject
            id="vip"
            type={args.type}
            options={args.searchApis[2]("VIP")}
          />
        );
      default:
        return (
          <LinkObject
            id="expariences"
            type={args.type}
            isAsync
            searchApi={args.searchApis[0]}
          />
        );
    }
  }

  function LinkObject(params) {
    const [selectedFromSearch, setSelectedFromSearch] = useState(null);

    return (
      <Root>
        <SearchContent
          isAsync={params.isAsync}
          searchApi={(name) => params.searchApi(name)}
          id={params.id}
          baseOptions={params.isAsync ? [] : params.options}
          selectedList={[]}
          setResults={(newArr) => {
            if (newArr.length > 0) {
              setSelectedFromSearch(newArr[0]);
            } else {
              setSelectedFromSearch(null);
            }
          }}
        />
        <div>
          <IconButton onClick={() => setType(null)} size="large">
            <Close />
          </IconButton>
          <IconButton
            onClick={() => {
              handleUpdateElement(selectedFromSearch);
            }}
            size="large"
          >
            <Check />
          </IconButton>
        </div>
      </Root>
    );
  }
}

export default DragAndDropList;
